import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { amountInWords, paymentMethodLabel, type PaymentMethod } from "@rentorng/core";
import { api } from "@/lib/api";
import { titleCase, useFormat } from "@/lib/format";
import { useApiMutation } from "@/lib/mutations";
import { useSession } from "@/lib/session";
import { Alert, Badge, Button, Card, CardTitle, Detail, ErrorState, PageHeader, Spinner, Table } from "@/components/ui";
import { AgencyMark } from "@/components/agency-mark";

/**
 * The receipt.
 *
 * Laid out to be printed and handed over — the print stylesheet drops the navigation and the
 * action buttons, so what comes out of the printer is the document, not a screenshot of an app.
 */

interface PaymentResponse {
  payment: {
    id: string;
    receiptNumber: string;
    amount: number;
    method: string;
    status: string;
    receivedOn: string;
    reference: string | null;
    notes: string | null;
    unallocated: number;
    reversedAt: string | null;
    reversalReason: string | null;
    createdAt: string;
  };
  party: { id: string; name: string; phone: string | null; email: string | null; address: string | null };
  recordedByName: string | null;
  allocations: Array<{ id: string; amount: number; invoiceId: string; number: string; dueDate: string; total: number; paid: number; unitLabel: string | null }>;
  org: {
    name: string;
    currency: string;
    phone: string | null;
    email: string | null;
    address: string | null;
    receiptFooter: string | null;
    showBalanceOnReceipt: boolean;
  };
  /** What this client still owes across every unsettled invoice, as at `asOf`. */
  outstanding: number;
  asOf: string;
}

export function PaymentDetailPage() {
  const { id } = useParams();
  /* The whole session: a receipt carries the signed-in agency's own mark, and it is not on the
     payment payload. */
  const session = useSession();
  const { vocab, can } = session;
  const fmt = useFormat();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["payment", id],
    queryFn: () => api.get<PaymentResponse>(`/payments/${id}`),
    enabled: Boolean(id),
  });

  const reverse = useApiMutation({
    mutationFn: (reason: string) => api.post(`/payments/${id}/reverse`, { reason }),
    invalidates: [["payment", id ?? ""], ["payments"], ["invoices"], ["dashboard"]],
  });

  const applyCredit = useApiMutation({
    mutationFn: () => api.post(`/payments/${id}/apply-credit`),
    invalidates: [["payment", id ?? ""], ["payments"], ["invoices"], ["dashboard"]],
  });

  if (isLoading) return <Spinner />;
  if (error || !data) return <ErrorState error={error} retry={() => refetch()} />;

  const { payment, party, org } = data;

  return (
    <div>
      <PageHeader
        title={payment.receiptNumber}
        back={{ to: "/app/payments", label: "Payments" }}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={payment.status === "reversed" ? "danger" : "success"}>{payment.status}</Badge>
            <Link to={`/app/clients/${party.id}`} className="text-ink-600 hover:underline">
              {party.name}
            </Link>
            <span className="text-ink-500">· {fmt.date(payment.receivedOn)}</span>
          </span>
        }
        actions={
          <>
            <Button variant="secondary" onClick={() => window.print()} className="no-print">
              Print receipt
            </Button>
            {can("payment.delete") && payment.status !== "reversed" ? (
              <Button
                variant="danger"
                className="no-print"
                disabled={reverse.isPending}
                onClick={() => {
                  const reason = window.prompt("Why is this being reversed? This stays on the record.");
                  if (reason) reverse.mutate(reason);
                }}
              >
                Reverse
              </Button>
            ) : null}
          </>
        }
      />

      {reverse.errorMessage ? <div className="mb-4 no-print"><Alert tone="danger">{reverse.errorMessage}</Alert></div> : null}
      {applyCredit.errorMessage ? <div className="mb-4 no-print"><Alert tone="danger">{applyCredit.errorMessage}</Alert></div> : null}

      <div className="grid gap-5 lg:grid-cols-3">
        <Card className="printable lg:col-span-2" padded={false}>
          <div className="border-b border-ink-200 p-4 sm:p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              {/* The letterhead. This is the document most often handed to somebody outside the
                  agency, which is why the mark was asked for at all. */}
              <AgencyMark name={org.name} logoUrl={session.org?.logoUrl}>
                {org.address ? <p className="text-xs text-ink-500">{org.address}</p> : null}
                {org.phone ? <p className="text-xs text-ink-500">{org.phone}</p> : null}
              </AgencyMark>
              <div className="text-right">
                <p className="text-xs uppercase tracking-wide text-ink-400">Receipt</p>
                <p className="text-lg font-semibold">{payment.receiptNumber}</p>
                <p className="text-xs text-ink-500">{fmt.date(payment.receivedOn, { long: true })}</p>
              </div>
            </div>

            <div className="mt-6">
              <p className="text-xs uppercase tracking-wide text-ink-400">Received from</p>
              <p className="text-base font-medium">{party.name}</p>
              {party.address ? <p className="text-xs text-ink-500">{party.address}</p> : null}
              {party.phone ? <p className="text-xs text-ink-500">{party.phone}</p> : null}
            </div>

            <div className="mt-6 rounded-lg bg-ink-50 p-4">
              <p className="text-xs uppercase tracking-wide text-ink-400">Amount</p>
              <p className="tabular text-3xl font-semibold text-brand-900">{fmt.money(payment.amount)}</p>
              <p className="mt-1 text-xs italic text-ink-600">{amountInWords(payment.amount, org.currency)}</p>
            </div>

            <dl className="mt-4">
              <Detail label="Method">{paymentMethodLabel(payment.method as PaymentMethod)}</Detail>
              {payment.reference ? <Detail label="Reference">{payment.reference}</Detail> : null}
              <Detail label="Recorded by">{data.recordedByName ?? "—"}</Detail>
              {payment.notes ? <Detail label="Notes">{payment.notes}</Detail> : null}
            </dl>

            {/* Switched on per agency in Settings. Labelled "as at" rather than "after this
                payment", because it is the balance now — reconstructing the balance at the
                moment of payment would mean replaying every allocation since. */}
            {org.showBalanceOnReceipt ? (
              <div
                className={`mt-4 rounded-[var(--radius-surface)] border px-4 py-3 ${
                  data.outstanding > 0 ? "border-warn-200 bg-warn-50" : "border-brand-200 bg-brand-50"
                }`}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="eyebrow">
                    {data.outstanding > 0 ? `Still outstanding as at ${fmt.date(data.asOf)}` : "Account status"}
                  </span>
                  <span
                    className={`tabular text-base font-semibold ${
                      data.outstanding > 0 ? "text-warn-700" : "text-brand-700"
                    }`}
                  >
                    {data.outstanding > 0 ? fmt.money(data.outstanding) : "Nothing outstanding"}
                  </span>
                </div>
              </div>
            ) : null}
          </div>

          {/*
            What this payment settled.

            When it settled nothing, this is a **statement of fact on a financial document**, not an
            empty screen. It read "Held as credit on the account. It will be offered against the
            next invoice." centred in the middle of the sheet, in the voice of an interface talking
            to a user — on a receipt handed to somebody who has just paid four million naira. A
            receipt says what happened to the money, in the same register as every other line on it.
          */}
          {data.allocations.length === 0 ? (
            <div className="border-t border-ink-200 p-4 sm:p-6">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <span className="eyebrow">Applied to</span>
                <span className="text-sm font-semibold">Credit on account</span>
              </div>
              <p className="mt-1 text-xs text-ink-500">
                No invoice was outstanding at the time of receipt. The amount is held on the account and will be
                applied to the next invoice raised.
              </p>
            </div>
          ) : (
            <Table
              head={
                <tr>
                  <th>Applied to</th>
                  <th>{titleCase(vocab.unit)}</th>
                  <th>Due</th>
                  <th className="num">Amount</th>
                </tr>
              }
            >
              {data.allocations.map((allocation) => (
                <tr key={allocation.id}>
                  <td>
                    <Link to={`/app/invoices/${allocation.invoiceId}`} className="link">
                      {allocation.number}
                    </Link>
                  </td>
                  <td className="text-ink-500">{allocation.unitLabel ?? "—"}</td>
                  <td className="text-ink-500">{fmt.date(allocation.dueDate)}</td>
                  <td className="num tabular">{fmt.money(allocation.amount)}</td>
                </tr>
              ))}
            </Table>
          )}

          {org.receiptFooter ? (
            <p className="border-t border-ink-200 p-4 text-xs text-ink-500 sm:p-6">{org.receiptFooter}</p>
          ) : null}
        </Card>

        <div className="space-y-5 no-print">
          {payment.unallocated > 0 ? (
            <Card>
              <CardTitle hint="Money already received is never lost — it sits here until there is something to apply it to.">
                Credit on account
              </CardTitle>
              <p className="tabular text-2xl font-semibold text-info-700">{fmt.money(payment.unallocated)}</p>
              {can("payment.record") ? (
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-3"
                  disabled={applyCredit.isPending}
                  onClick={() => applyCredit.mutate(undefined as never)}
                >
                  {applyCredit.isPending ? "Applying…" : "Apply to open invoices"}
                </Button>
              ) : null}
            </Card>
          ) : null}

          {payment.status === "reversed" ? (
            <Card>
              <CardTitle>Reversed</CardTitle>
              <p className="text-sm text-ink-600">{payment.reversalReason}</p>
              <p className="mt-1 text-xs text-ink-500">{fmt.dateTime(payment.reversedAt)}</p>
              <p className="mt-2 text-xs text-ink-500">
                The receipt stays on the record because its number was quoted to somebody. The invoices it touched have
                been recalculated.
              </p>
            </Card>
          ) : null}

          <Card>
            <CardTitle>Send it</CardTitle>
            <div className="flex flex-wrap gap-2">
              {party.email ? (
                <a
                  className="btn btn-secondary btn-sm"
                  href={`mailto:${party.email}?subject=${encodeURIComponent(`Receipt ${payment.receiptNumber}`)}&body=${encodeURIComponent(
                    `Dear ${party.name.split(" ")[0]},\n\nThank you. We have received ${fmt.money(payment.amount)} on ${fmt.date(payment.receivedOn)}.\n\nReceipt ${payment.receiptNumber}\n\n${org.name}`,
                  )}`}
                >
                  Email receipt
                </a>
              ) : null}
              {party.phone ? (
                <a
                  className="btn btn-secondary btn-sm"
                  target="_blank"
                  rel="noreferrer"
                  href={`https://wa.me/${party.phone.replace(/\D/g, "")}?text=${encodeURIComponent(
                    `Receipt ${payment.receiptNumber} — ${fmt.money(payment.amount)} received on ${fmt.date(payment.receivedOn)}. Thank you. ${org.name}`,
                  )}`}
                >
                  WhatsApp
                </a>
              ) : null}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
